import { t } from '../i18n/translate';

export type WorkshopCommunityViewerRole = 'confirmed' | 'interest';

/** Main card header, e.g. "(11 with 5 still deciding)" or "(6 confirmed)". */
export function formatParticipantsCardHeaderCount(
  confirmedCount: number,
  interestCount: number,
  workshopStarted: boolean
): string {
  if (workshopStarted || interestCount === 0) {
    return confirmedCount === 1
      ? t('workshop.2026.roster.confirmed_1')
      : t('workshop.2026.roster.confirmed_n', { count: confirmedCount });
  }
  const total = confirmedCount + interestCount;
  return interestCount === 1
    ? t('workshop.2026.roster.deciding_1', { total })
    : t('workshop.2026.roster.deciding_n', { total, count: interestCount });
}

export function formatCommunityCardTitle(
  viewerRole: WorkshopCommunityViewerRole,
  confirmedCount: number,
  interestCount = 0,
  workshopStarted = false
): string {
  if (viewerRole === 'interest') {
    return t('workshop.2026.roster.community_title');
  }
  return t('workshop.2026.roster.participants_title', {
    headerCount: formatParticipantsCardHeaderCount(
      confirmedCount,
      interestCount,
      workshopStarted
    ),
  });
}

export function formatCommunityCardSubtitle(
  viewerRole: WorkshopCommunityViewerRole,
  confirmedCount: number,
  interestCount: number,
  workshopStarted: boolean
): string | undefined {
  if (viewerRole === 'confirmed') {
    return undefined;
  }

  if (workshopStarted) {
    return confirmedCount === 1
      ? t('workshop.2026.roster.confirmed_short_1')
      : t('workshop.2026.roster.confirmed_short_n', { count: confirmedCount });
  }

  const confirmedPart =
    confirmedCount === 1
      ? t('workshop.2026.roster.confirmed_short_1')
      : t('workshop.2026.roster.confirmed_short_n', { count: confirmedCount });
  const interestPart =
    interestCount === 1
      ? t('workshop.2026.roster.interest_short_1')
      : t('workshop.2026.roster.interest_short_n', { count: interestCount });
  return t('workshop.2026.roster.subtitle_combo', {
    confirmed: confirmedPart,
    interest: interestPart,
  });
}

export function formatConfirmedRosterNames(
  visibleNames: string[],
  namesHiddenCount: number,
  confirmedCount: number
): string {
  if (confirmedCount === 0) {
    return t('workshop.2026.roster.no_confirmed');
  }

  if (visibleNames.length === 0) {
    return namesHiddenCount === 1
      ? t('workshop.2026.roster.joining_1')
      : t('workshop.2026.roster.joining_n', { count: namesHiddenCount });
  }

  const names = visibleNames.join(', ');
  if (namesHiddenCount > 0) {
    return namesHiddenCount === 1
      ? t('workshop.2026.roster.plus_1_other', { names })
      : t('workshop.2026.roster.plus_n_others', { names, count: namesHiddenCount });
  }
  return t('workshop.2026.roster.names_period', { names });
}

export function formatInterestListSummary(interestCount: number): string {
  if (interestCount === 0) {
    return t('workshop.2026.roster.interest_empty');
  }
  if (interestCount === 1) {
    return t('workshop.2026.roster.interest_1');
  }
  return t('workshop.2026.roster.interest_n', { count: interestCount });
}

export function formatInterestedViewerLines(
  confirmedCount: number,
  interestCount: number,
  workshopStarted: boolean,
  includesViewer: boolean
): string[] {
  if (workshopStarted) {
    if (confirmedCount === 1) {
      return [t('workshop.2026.roster.viewer_confirmed_1')];
    }
    return [t('workshop.2026.roster.viewer_confirmed_n', { count: confirmedCount })];
  }

  const lines: string[] = [];
  if (confirmedCount === 1) {
    lines.push(t('workshop.2026.roster.viewer_planning_1'));
  } else {
    lines.push(t('workshop.2026.roster.viewer_planning_n', { count: confirmedCount }));
  }

  if (interestCount > 0) {
    const suffix = includesViewer ? t('workshop.2026.roster.including_you') : '';
    if (interestCount === 1) {
      lines.push(t('workshop.2026.roster.viewer_interest_1', { suffix }));
    } else {
      lines.push(t('workshop.2026.roster.viewer_interest_n', { count: interestCount, suffix }));
    }
  }

  return lines;
}
